'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { isElectron, gateway } from './api';

/**
 * Disk-backed chat history (per project slug).
 *
 * In Electron: persists to a JSON file in the app's userData directory via
 * IPC (chat:history:read / chat:history:write). No 5 MB cap, atomic writes,
 * survives any UI bug that might wipe localStorage.
 *
 * In browser: falls back to localStorage at the same key the legacy code
 * used (`beast:chat:messages:<slug>`), so behavior is unchanged when run
 * outside Electron.
 *
 * Initial state is read SYNCHRONOUSLY from localStorage on first render so
 * the chat doesn't flash empty during the (potentially MB-sized) async
 * disk read. Disk is then queried in the background and its result wins
 * if it differs (disk is the source of truth in Electron).
 */
// One-time sweep of legacy multi-MB localStorage chat mirrors. They filled
// the 10MB origin quota, which made EVERY localStorage write app-wide fail
// silently (active project could never persist → chat kept reverting to
// General). History is NEVER deleted: each mirror is migrated to the disk
// files first (the source of truth) and only removed once disk has it.
let sweepStarted = false;
async function sweepLegacyMirrors() {
  if (sweepStarted) return;
  sweepStarted = true;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('beast:chat:messages:')) keys.push(k);
    }
    for (const k of keys) {
      const slug = k.slice('beast:chat:messages:'.length);
      let cached: unknown[] = [];
      try { cached = JSON.parse(localStorage.getItem(k) || '[]'); } catch {}
      if (!Array.isArray(cached) || cached.length === 0) {
        try { localStorage.removeItem(k); } catch {}
        continue;
      }
      try {
        const res: any = await gateway.chatHistory.read(slug);
        const disk = Array.isArray(res?.messages) ? res.messages : [];
        if (disk.length < cached.length) {
          await gateway.chatHistory.write(slug, cached);
        }
        localStorage.removeItem(k);
        console.log(`[beast] chat mirror migrated to disk, quota freed: ${slug}`);
      } catch {
        // gateway/IPC unreachable — keep the mirror, retry next launch
      }
    }
  } catch {}
}

// Compaction thresholds: live file keeps the last COMPACT_KEEP messages;
// anything beyond COMPACT_THRESHOLD triggers a one-time archive split.
const COMPACT_THRESHOLD = 300;
const COMPACT_KEEP = 200;

export function useChatHistory<T extends { id: string }>(
  slug: string,
): [T[], (update: T[] | ((prev: T[]) => T[])) => void, boolean] {
  const lsKey = `beast:chat:messages:${slug}`;

  // Always start with [] so server-rendered HTML matches client first render
  // (no SSR hydration mismatch). Real data is loaded in useEffect below —
  // first the synchronous localStorage cache (fast, ~1 frame), then disk
  // (Electron source of truth) overrides if it differs. The Chat component
  // gates its EmptyState on `hydrated` so users never see "no messages"
  // during this brief load window.
  const [messages, setMessagesState] = useState<T[]>([]);

  const [hydrated, setHydrated] = useState(false);
  const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slugRef = useRef(slug);
  slugRef.current = slug;
  // Tracks the slug we have actually finished hydrating from disk for. Writes
  // are gated on this matching the current slug — prevents the brief window
  // where we mount with one slug, the project's real slug arrives, and a
  // mid-flight setMessages would otherwise persist [] under the wrong key.
  const hydratedSlugRef = useRef<string | null>(null);

  // Hydrate from disk (Electron) or localStorage (browser) on mount and on
  // slug change. Disk is the source of truth; localStorage is a cache.
  useEffect(() => {
    let cancelled = false;
    setHydrated(false);

    // Step 1 — synchronous localStorage cache read for instant first-paint.
    // This runs in useEffect (post-mount) so it doesn't fight SSR hydration.
    let cachedFromLS: T[] = [];
    try {
      const cached = localStorage.getItem(lsKey);
      if (cached) {
        const arr = JSON.parse(cached);
        if (Array.isArray(arr)) cachedFromLS = arr as T[];
      }
    } catch {}
    if (cachedFromLS.length > 0) setMessagesState(cachedFromLS);

    async function load() {
      // Both Electron (IPC) and Browser-via-tunnel (HTTP) now go through
      // gateway.chatHistory, which reads from the SAME disk files in
      // ~/Library/Application Support/beast/chat-history/<slug>.json. The
      // Mini App on the phone therefore sees the same chats as the desktop.
      try {
        const res: any = await gateway.chatHistory.read(slug);
        if (cancelled) return;
        const fromDisk: T[] = Array.isArray(res?.messages) ? res.messages : [];
        if (fromDisk.length === 0 && cachedFromLS.length > 0) {
          // Disk empty but cache has data — migrate cache → disk so the next
          // device to open this chat sees the data. Drop the mirror only
          // after the disk write succeeds (history is never lost).
          gateway.chatHistory.write(slug, cachedFromLS)
            .then(() => { try { localStorage.removeItem(lsKey); } catch {} })
            .catch(() => {});
          // Keep cachedFromLS in state; don't overwrite with [].
        } else if (fromDisk.length > COMPACT_THRESHOLD) {
          // Perf compaction: general.json reached 38MB — every hydrate parsed it
          // all and every new message rewrote it all. Move older messages to an
          // immutable archive file ONCE (history is never deleted), keep the
          // recent tail as the live file. Trim only AFTER the archive write
          // succeeds — a failed archive means we keep the full file untouched.
          const recent = fromDisk.slice(-COMPACT_KEEP) as T[];
          const older = fromDisk.slice(0, fromDisk.length - COMPACT_KEEP);
          setMessagesState(recent);
          const archiveKey = `${slug}--archive-${Date.now()}`;
          // RACE GUARD (2026-07-07 scar): the old code wrote back the `recent`
          // SNAPSHOT — any message written by another writer (voice mirror,
          // live work log) between our read and our write was ERASED; Boss
          // watched live work vanish mid-read. Now: archive first, then
          // RE-READ the live file and remove only the archived ids — never
          // restore a stale snapshot.
          const archivedIds = new Set(older.map((m: any) => m?.id));
          gateway.chatHistory.write(archiveKey, older as unknown[])
            .then(async () => {
              const latest: any = await gateway.chatHistory.read(slug);
              const live: any[] = Array.isArray(latest?.messages) ? latest.messages : [];
              await gateway.chatHistory.write(slug, live.filter(m => !archivedIds.has(m?.id)));
            })
            .then(() => console.log(`[beast] chat compacted: ${slug} — ${older.length} msgs → ${archiveKey}`))
            .catch(() => {});
          try { localStorage.removeItem(lsKey); } catch {}
        } else {
          setMessagesState(fromDisk);
          // Disk is the source of truth — do NOT mirror multi-MB chats into
          // localStorage (mirrors filled the 10MB quota and silently broke
          // every other write, incl. the active project).
          try { localStorage.removeItem(lsKey); } catch {}
        }
        hydratedSlugRef.current = slug;
        if (!cancelled) setHydrated(true);
      } catch {
        // Gateway/IPC failed — keep whatever localStorage gave us
        hydratedSlugRef.current = slug;
        if (!cancelled) setHydrated(true);
      }
    }

    load();
    sweepLegacyMirrors(); // frees the quota (migrates to disk first)
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Debounced persist on every messages change. Writes to BOTH disk
  // (Electron) and localStorage so reload-without-electron still has data.
  const setMessages = useCallback((update: T[] | ((prev: T[]) => T[])) => {
    setMessagesState(prev => {
      const next = typeof update === 'function' ? (update as (prev: T[]) => T[])(prev) : update;

      // Suppress writes if our hydrated slug doesn't match — we're mid-
      // transition between projects and an in-flight setMessages would
      // otherwise persist the wrong array under the wrong key.
      if (hydratedSlugRef.current !== slugRef.current) {
        return next;
      }

      // No localStorage mirror — the disk write below is the store; mirrors
      // blew the 10MB quota. Mid-stream refresh recovery still works via the
      // 3s auto-save (which also lands on disk) + /chat/last reconnect.

      // Debounced disk write (Electron via IPC, browser via HTTP gateway).
      // Capture the slug NOW — if the user switches projects before the
      // debounce fires, slugRef points at the NEW slug and this write would
      // persist chat A's messages into chat B's history file. Drop instead.
      const writeSlug = slugRef.current;
      if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
      writeTimerRef.current = setTimeout(async () => {
        writeTimerRef.current = null;
        if (writeSlug !== slugRef.current) return;
        // MERGE-ON-WRITE: other writers append to this file too (voice
        // mirror, voice-delegated task + live work log). A blind overwrite
        // of our state ERASED their messages before the merge-poll could
        // pick them up (Boss never saw delegated tasks). Union by id —
        // never delete a message this component has not seen.
        let out: unknown[] = next as unknown[];
        try {
          const res: any = await gateway.chatHistory.read(writeSlug);
          const disk: T[] = Array.isArray(res?.messages) ? res.messages : [];
          const haveIds = new Set((next as T[]).map(m => m.id));
          const external = disk.filter(m => m?.id && !haveIds.has(m.id));
          // voicetask_*/voice_* messages are OWNED by external writers (live
          // work log updates in place) — the disk version always wins for
          // them, otherwise our stale copy would roll back their updates.
          const diskById = new Map(disk.map(m => [m.id, m]));
          const merged = (next as T[]).map(m =>
            (m.id?.startsWith('voicetask_') || m.id?.startsWith('voice_')) && diskById.has(m.id)
              ? (diskById.get(m.id) as T) : m,
          );
          if (external.length || merged.some((m, i) => m !== (next as T[])[i])) {
            out = ([...merged, ...external] as any[])
              .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
          }
        } catch { /* disk unreadable — write our state as before */ }
        if (writeSlug !== slugRef.current) return;
        gateway.chatHistory.write(writeSlug, out).catch(() => {});
      }, 300);

      return next;
    });
  }, [lsKey]);

  return [messages, setMessages, hydrated];
}
